import { Link } from "react-router";
import { Fingerprint } from "lucide-react";
import { useCookieConsent } from "../contexts/CookieConsentContext";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

function ConsentActions() {
  const { acceptAnalytics, declineAnalytics } = useCookieConsent();

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
      <Button variant="outline" onClick={declineAnalytics}>
        Nur notwendige Cookies
      </Button>
      <Button onClick={acceptAnalytics}>Optionale Cookies akzeptieren</Button>
    </div>
  );
}

function ConsentCopy() {
  return (
    <div className="space-y-3 text-sm leading-relaxed text-stone-700">
      <p>
        Wir verwenden optionale Cookies nur nach Ihrer ausdrücklichen Einwilligung. Damit
        können wir die Nutzung der Website auswerten und unser Angebot verbessern.
      </p>
      <p>
        Standardmäßig bleiben optionale Cookies deaktiviert. Sie können Ihre Auswahl jederzeit
        über den Link &quot;Cookie-Einstellungen&quot; im Footer ändern.
      </p>
      <p>
        Details finden Sie in unserer{" "}
        <Link
          to="/datenschutz"
          className="font-medium text-stone-950 underline decoration-stone-300 underline-offset-4"
        >
          Datenschutzerklärung
        </Link>
        .
      </p>
    </div>
  );
}

export function CookieConsentManager() {
  const {
    consent,
    hasAnalyticsConfigured,
    isPreferencesOpen,
    isReady,
    closePreferences,
    openPreferences,
  } = useCookieConsent();

  if (!hasAnalyticsConfigured || !isReady) {
    return null;
  }

  return (
    <>
      {consent === "unset" ? (
        <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-4 sm:px-6 sm:pb-6">
          <div className="mx-auto max-w-5xl border border-stone-400 bg-white shadow-[0_32px_120px_rgba(15,23,42,0.3)] ring-1 ring-black/10 backdrop-blur">
            <div className="flex flex-col gap-5 bg-[linear-gradient(135deg,#ffffff_0%,#fafaf9_55%,#f5f5f4_100%)] px-5 py-5 sm:px-6 sm:py-6 lg:flex-row lg:items-end lg:justify-between lg:px-8">
              <div className="max-w-3xl">
                <p className="inline-flex rounded-full border border-stone-300 bg-stone-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-stone-600">
                  Cookie-Einstellungen
                </p>
                <h2 className="mt-2 text-xl leading-tight text-stone-950">
                  Sie entscheiden, welche Cookies verwendet werden.
                </h2>
                <div className="mt-3">
                  <ConsentCopy />
                </div>
              </div>
              <ConsentActions />
            </div>
          </div>
        </div>
      ) : null}

      {consent !== "unset" && !isPreferencesOpen ? (
        <button
          type="button"
          onClick={openPreferences}
          aria-label="Cookie-Einstellungen öffnen"
          title="Cookie-Einstellungen"
          className="fixed bottom-4 left-4 z-40 inline-flex size-12 items-center justify-center rounded-full border border-stone-300 bg-white text-stone-700 shadow-[0_16px_40px_rgba(15,23,42,0.2)] transition hover:border-stone-400 hover:text-stone-950 hover:shadow-[0_20px_50px_rgba(15,23,42,0.28)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 focus-visible:ring-offset-2"
        >
          <Fingerprint className="size-5" />
        </button>
      ) : null}

      <Dialog open={isPreferencesOpen} onOpenChange={(open) => !open && closePreferences()}>
        <DialogContent className="max-w-xl rounded-3xl border-stone-200 p-0">
          <div className="space-y-6 p-6 sm:p-8">
            <DialogHeader className="text-left">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-stone-500">
                Cookie-Einstellungen
              </p>
              <DialogTitle className="text-2xl leading-tight text-stone-950">
                Cookie-Einstellungen verwalten
              </DialogTitle>
              <DialogDescription className="sr-only">
                Verwalten Sie Ihre Einwilligung für optionale Cookies.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
              <p className="text-sm font-medium text-stone-950">Aktueller Status</p>
              <p className="mt-1 text-sm text-stone-700">
                {consent === "granted"
                  ? "Optionale Cookies sind aktuell aktiviert."
                  : "Es sind aktuell nur notwendige Cookies aktiv."}
              </p>
            </div>

            <ConsentCopy />
            <ConsentActions />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
